import * as React from 'react'
import { styled, css } from 'next-yak'

import {
  Polymorphic,
  type PolymorphicComponent,
  type PolymorphicProps,
  type YakComponentOf,
} from '@owl-nest/yak/extra-runtime'

import * as behaviours from '../behaviours'
import * as bp from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'

import * as S from '../styles'

type ExtraWysiwygContentProps = {
  className?: string
  /** @default 'eager' */
  loading?: 'eager' | 'lazy'
  nativeLazyLoad?: boolean
  $textSize?: 'small' | 'medium' | 'large'
}

type WysiwygContentProps = ExtraWysiwygContentProps & PolymorphicProps<'div'>

const WysiwygContentComponent = React.forwardRef<HTMLDivElement, WysiwygContentProps>(function WysiwygContentComponent(
  { loading = 'eager', nativeLazyLoad, dangerouslySetInnerHTML, className, ...rest },
  ref,
): React.ReactElement<WysiwygContentProps> {
  if (loading === 'lazy') {
    return (
      <behaviours.LazyWysiwyg nativeLazyLoad={nativeLazyLoad}>
        <Polymorphic.div {...rest} ref={ref} className={className} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      </behaviours.LazyWysiwyg>
    )
  } else {
    return (
      <Polymorphic.div {...rest} ref={ref} className={className} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
    )
  }
})

export const WysiwygContent = styled(WysiwygContentComponent)<WysiwygContentProps>`
  font-family: ${fonts.PRIMARY_FONT};
  word-wrap: break-word;
  word-break: break-word;

  a {
    ${S.link._baseStyle}
    ${S.link._primaryStyle}
    color: ${({ theme }) => S.link.getPrimaryTintedColor(theme)};
    font-weight: 300;
    overflow-wrap: break-word;
  }

  blockquote {
    ${S.heading.styles.L};
    display: inline-flex;
    line-height: 38px;
    margin-left: 0;

    &::before {
      border-left: 2px ${colors.PRIMARY_SAND_200} solid;
      content: '';
      padding-right: 26px;
    }
  }

  h3 {
    ${S.heading.styles.S};
  }

  h4 {
    ${S.heading.styles.XS};
  }

  h5,
  h6 {
    ${S.heading.styles.XXXXS};
  }

  @media screen and ${bp.TABLET} {
    h1,
    h2 {
      ${S.heading.styles.M};
    }

    h3 {
      ${S.heading.styles.S};
    }

    h4 {
      ${S.heading.styles.XS};
    }

    h5 {
      ${S.heading.styles.XXS};
    }

    h6 {
      ${S.heading.styles.XXXS};
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${colors.TEXT_PRIMARY};
  }

  iframe,
  img {
    max-width: 100%;
  }

  img {
    // HACK: Force images to ignore the inline height attribute assigned to them by CKE and follow 'aspect-ratio'.
    //  Our media elements are always constrained in width. So, following the height attribute makes them break out of
    //  the original aspect ratio.
    height: auto;
  }

  :global(.image) {
    margin: 0.9em auto;

    img {
      margin: 0 auto;
    }
  }

  img[align='left'],
  img[style*='float:left'],
  img[style*='float: left'] {
    margin-right: 40px;
  }

  :global(.image-style-align-left) {
    margin: 0.9em 40px 0.9em auto;
    float: left;
  }

  img[align='right'],
  img[style*='float:right'],
  img[style*='float: right'] {
    margin-left: 40px;
  }

  :global(.image-style-align-right) {
    margin: 0.9em auto 0.9em 40px;
    float: right;
  }

  :global(.image-style-block-align-left) {
    margin-left: 0;
    margin-right: auto;
  }

  :global(.image-style-block-align-right) {
    margin-right: 0;
    margin-left: auto;
  }

  figure {
    margin-left: 0;
    margin-right: 0;

    img {
      display: block;
    }
  }

  :global(.image-column) figure {
    margin-top: 0;
    margin-bottom: 0;
    display: flex;
    justify-content: center;
    align-items: baseline;
  }

  :global(.media) :global(.media-wrapper) {
    text-align: center;

    :global(.embed-sizer) {
      margin: 0 auto;

      iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  li {
    ${({ $textSize }) => {
      if ($textSize === 'small') {
        return css`
          ${S.copy.styles.S.regular};
        `
      } else if ($textSize === 'large') {
        return css`
          ${S.copy.styles.L.light};
        `
      } else {
        return css`
          ${S.copy.styles.M.light};
        `
      }
    }}
    & > p {
      display: inline;
    }
  }

  ol,
  p,
  ul:not(:global(.ck-list)) {
    ${({ $textSize }) => {
      if ($textSize === 'small') {
        return css`
          ${S.copy.styles.S.regular};
        `
      } else if ($textSize === 'large') {
        return css`
          ${S.copy.styles.L.light};
        `
      } else {
        return css`
          ${S.copy.styles.M.light};
        `
      }
    }}

    margin: 1em 0;
  }

  ol,
  strong,
  ul:not(:global(.ck-list)) {
    color: inherit;
  }

  ol:not(:global(.ck-list)),
  ul:not(:global(.ck-list)) {
    padding-left: 20px;
  }

  ol:not(:global(.ck-list)) {
    counter-reset: item;

    & > li {
      counter-increment: item;
      list-style-type: none;
      margin: 0;
      padding: 0 0 0 2em;
      text-indent: -2em;

      &::before {
        content: counter(item) '.';
        display: inline-block;
        font-weight: 500;
        padding-right: 0.5em;
        text-align: right;
        width: 1em;
        vertical-align: top;
      }
    }
  }

  && {
    strong {
      font-weight: bold;
      font-size: inherit;
    }
  }

  ul:not(:global(.ck-list)) {
    list-style: none;
  }

  ul:not(:global(.ck-list)) > li::before {
    content: '•';
    padding-right: 0.5em;
    vertical-align: top;
  }
` as YakComponentOf<PolymorphicComponent<'div', ExtraWysiwygContentProps>>

WysiwygContent.displayName = 'WysiwygContent'
